import React from 'react';

import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import {accessGroupEnum} from '../../../serverClientShared/sharedEnums';
import {SpecialBadge, DirectBadge} from './AccessCheckbox';
import {MemberAvatarTooltipContent} from './MixedDirectBadge';

const AVATAR_TOOLTIP_CLASSES = '!bg-background-normal !border !border-panels-normal !shadow-lg !rounded-input !p-0';
const TOOLTIP_PROPS = {
    tooltip: {
        className: '!text-base !text-main-normal !bg-background-normal !border !border-panels-normal !shadow-lg !rounded-input',
    },
};

const GroupLabel = ({sources}) => {
    if (!sources.length) return null;
    const firstName = sources[0].sourceName;
    const extra = sources.length - 1;

    return (
        <Tooltip
            title="Access inherited through a group"
            placement="top"
            enterDelay={300}
            componentsProps={TOOLTIP_PROPS}
        >
            <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-menu text-xs text-main-disabled whitespace-nowrap cursor-default"
                style={{backgroundColor: 'color-mix(in srgb, var(--main-disabled-color) 25%, transparent)'}}
            >
                <InfoOutlinedIcon style={{fontSize: 12}} />
                <span>via {firstName}{extra > 0 ? ` +${extra}` : ''}</span>
            </span>
        </Tooltip>
    );
};

const getPartialAndFullSources = (sources, groupMembership, targetCount) => {
    const partial = sources.filter(s => {
        const members = groupMembership.get(s.sourceId);
        return !members || members.size < targetCount;
    });
    const full = sources.filter(s => {
        const members = groupMembership.get(s.sourceId);
        return members && members.size >= targetCount;
    });
    return {partial, full};
};

const getPartialMembers = (partialSources, groupMembership, targets) => {
    const ids = new Set();
    for (const s of partialSources) {
        const members = groupMembership.get(s.sourceId);
        if (members) members.forEach(id => ids.add(id));
    }
    return targets.filter(t => ids.has(t.id));
};

const BulkGroupLabel = ({sources, groupMembership, targetCount, targets, inheritedIncludeAll}) => {
    if (!sources.length) return null;
    const {partial, full} = getPartialAndFullSources(sources, groupMembership, targetCount);
    const hidePartial = full.length > 0 && inheritedIncludeAll;
    const partialMembers = hidePartial ? [] : getPartialMembers(partial, groupMembership, targets);

    return (
        <>
            {full.length > 0 && <GroupLabel sources={full} />}
            {!hidePartial && partial.length > 0 && (
                <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-menu text-xs whitespace-nowrap"
                    style={{backgroundColor: 'color-mix(in srgb, var(--caution-warning-color) 10%, transparent)', color: 'var(--caution-warning-color)'}}
                >
                    <Tooltip
                        title={<MemberAvatarTooltipContent members={partialMembers} />}
                        placement="top"
                        componentsProps={{tooltip: {className: AVATAR_TOOLTIP_CLASSES}}}
                    >
                        <InfoOutlinedIcon style={{fontSize: 12, cursor: 'pointer'}} />
                    </Tooltip>
                    <span>via {partial[0].sourceName}{partial.length > 1 ? ` +${partial.length - 1}` : ''}</span>
                </span>
            )}
        </>
    );
};

export const AccessSourceLabel = ({accessInfo, hasDirectAccess, isBulkMode, groupMembership, targetCount, targets, nodeType, isPublicEffective}) => {
    if (!accessInfo) return null;

    const inheritedFrom = accessInfo.inheritedFrom || [];
    const specialIds = [accessGroupEnum.WORKSPACE_PUBLIC, accessGroupEnum.GUEST];
    const specialSources = inheritedFrom.filter(s => specialIds.includes(s.sourceId));
    const groupSources = inheritedFrom.filter(s => !specialIds.includes(s.sourceId));

    const isPublic = specialSources.some(s => s.sourceId === accessGroupEnum.GUEST);
    const isShared = isPublicEffective && specialSources.some(s => s.sourceId === accessGroupEnum.WORKSPACE_PUBLIC);
    const hasInherited = inheritedFrom.length > 0;
    const showDirect = hasDirectAccess && hasInherited;

    if (!hasInherited) return null;
    if (!showDirect && !isPublic && !isShared && groupSources.length === 0) return null;

    return (
        <div className="ml-auto flex items-center gap-1.5 shrink-0">
            {showDirect && <DirectBadge />}
            {isPublic && <SpecialBadge label="PUBLIC" />}
            {isShared && <SpecialBadge label="SHARED" nodeType={nodeType} />}
            {groupSources.length > 0 && (
                isBulkMode
                    ? <BulkGroupLabel sources={groupSources} groupMembership={groupMembership} targetCount={targetCount} targets={targets} inheritedIncludeAll={accessInfo.inheritedIncludeAll} />
                    : <GroupLabel sources={groupSources} />
            )}
        </div>
    );
};
